import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { api } from "@/lib/api";
import { DISH_CATEGORIES } from "@/lib/constants";
import { CountUp } from "@/components/CountUp";

export function TableSection({ enabled }) {
  const [open, setOpen] = useState(null);
  const { data } = useQuery({
    queryKey: ["table"],
    queryFn: async () => (await api.get("/table")).data,
    enabled,
    retry: false,
  });
  if (!enabled) return null;
  const cats = data?.categories || {};
  const dishes = data?.dishes || [];
  const total = data?.total || 0;

  const toggle = (id) => setOpen((cur) => (cur === id ? null : id));

  return (
    <section id="table" className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24 scroll-mt-20">
      <div className="text-center mb-10">
        <p className="text-eyebrow text-gold">The Potluck</p>
        <h2 className="font-serif text-3xl sm:text-4xl text-espresso mt-2">What's Coming to the Table</h2>
        <p className="text-inkSoft mt-3">
          {total > 0 ? <><CountUp value={total} className="font-semibold text-espresso" /> dishes already claimed · tap a category to see what friends are bringing</> : "Be the first to bring something delicious"}
        </p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-5">
        {DISH_CATEGORIES.map((c) => {
          const count = cats[c.id] || 0;
          const items = dishes.filter((d) => d.category === c.id);
          const isOpen = open === c.id;
          return (
            <div key={c.id} className="contents">
              <button
                onClick={() => count > 0 && toggle(c.id)}
                disabled={count === 0}
                className={`rounded-3xl bg-white border shadow-soft p-6 text-center transition-all ${isOpen ? "border-gold shadow-lift -translate-y-1" : "border-hair"} ${count > 0 ? "hover:shadow-lift hover:-translate-y-1 cursor-pointer" : "opacity-70 cursor-default"}`}
                data-testid={`table-cat-${c.id.replace(/[^a-zA-Z]/g, "")}`}
              >
                <div className="text-4xl mb-3">{c.icon}</div>
                <div className="font-serif text-3xl text-espresso"><CountUp value={count} /></div>
                <div className="text-eyebrow text-inkMuted mt-1">{c.label}</div>
                {count > 0 && (
                  <div className="mt-3 flex items-center justify-center gap-1 text-xs font-semibold text-gold">
                    {isOpen ? "Hide" : "View dishes"} <ChevronDown size={14} className={`transition-transform ${isOpen ? "rotate-180" : ""}`} />
                  </div>
                )}
              </button>
              <AnimatePresence>
                {isOpen && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                    className="col-span-2 md:col-span-4 overflow-hidden"
                    data-testid={`table-dishes-${c.id.replace(/[^a-zA-Z]/g, "")}`}
                  >
                    <div className="rounded-3xl bg-cream2 border border-hair p-5 sm:p-6">
                      <p className="text-eyebrow text-inkMuted mb-4">{c.icon} {c.label} · {count}</p>
                      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {items.map((d, i) => (
                          <motion.div
                            key={i}
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: i * 0.04 }}
                            className="flex items-center justify-between gap-3 rounded-2xl bg-white border border-hair px-4 py-3"
                            data-testid={`dish-item-${c.id.replace(/[^a-zA-Z]/g, "")}-${i}`}
                          >
                            <span className="text-espresso font-medium">{d.name}</span>
                            <span className="text-xs text-inkMuted whitespace-nowrap">{d.by}</span>
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </section>
  );
}
